import Loader from '../../components/Loader';
import {T} from '../../utils/I18n/I18n';
import {useTranslation} from "react-i18next";
import clsx from 'clsx';
import moment from 'moment';
import React, { memo, useContext, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useForm } from 'react-hook-form'; // Usunięto FormContext i useFormContext
import { Link, useNavigate } from 'react-router-dom';
import { FETCH_CONFIG } from '../../components/Product/ProductSidebarClub/ProductSidebarClub';
import { useCartContext } from '../../context/CartContext';
import { useUserContext } from '../../context/UserContext';
import useCartMutation from '../../hooks/useCartMutation';
import { APPLY_LOYALTY_POINTS, GET_CART } from '../../queries/cart';
import { CONNECT_CARD, CREATE_CARD, GET_CARD } from '../../queries/customer';
import './CheckoutLoyalty.scss';
import { isSI } from "../../utils/isHr";

const CheckoutLoyaltyPoints = () => {
  const { cart, loading: loadingCart } = useCartContext();
  const grandTotal = (cart && cart.prices.grand_total.value) || 0;
  const { data: config, loading: loadingConfig } = useQuery(FETCH_CONFIG);
  const rate = config && config.backendConfig.shop.loyaltyConversion;
  const loyaltyPoints = rate && (Math.floor(grandTotal / rate) || 0);
  const loyaltyPointsBanking = loyaltyPoints && loyaltyPoints * 2;

  if (loadingConfig || loadingCart) {
    return <Loader mt="lg" height="lg" />;
  }

  return (
      <div className="checkout-loyalty__points">
        <p className="loyalty-message">
          <T id="cartSummary.loyaltyMessage" />
        </p>

        {!isSI && (
            <p className="loyalty-points">
              <strong>{loyaltyPointsBanking}</strong> <T id="cartSummary.loyaltyPointsNoCard" />
            </p>
        )}

        <p className="loyalty-points">
          <strong>{loyaltyPoints}</strong> <T id="cartSummary.loyaltyPointsCard" />
        </p>
      </div>
  );
};

const CheckoutLoyaltyRegister = () => {
  const navigate = useNavigate(); // Zmiana z history na navigate

  const onRegisterHandler = () => {
    navigate('/customer/registration', {
      state: { redirectToCheckout: true }, // Zaktualizowano przekazywanie stanu w v6
    });
  };

  return (
      <div className="checkout-loyalty__button-wrapper">
        <button type="button" className="checkout__btn checkout__btn--loyalty float-right" onClick={onRegisterHandler}>
          <T id="club.register" />
        </button>
      </div>
  );
};

const LoyaltyFormInput = ({ name, label, type = 'text', min, max, register, errors, formState }) => {
  const inputProps = { name, type, min, max };
  const requiredError = errors[name] && errors[name].type === 'required';

  const className = clsx([
    'checkout-loyalty__field checkout__main__form__field',
    requiredError && formState.isSubmitted && 'checkout-loyalty__field--required',
  ]);

  return (
      <div className={className}>
        <label htmlFor={name}>
          <T id={label} /> <span className="required">*</span>
        </label>

        <input id={name} {...inputProps} {...register(name, { required: true })} />

        {requiredError && formState.isSubmitted && (
            <div className="validation-message">
              <T id="form.requiredField" />.
            </div>
        )}
      </div>
  );
};

const LoyaltyFormInputSelect = ({ name, label, options, register, errors, formState }) => {
  const requiredError = errors[name] && errors[name].type === 'required';

  const className = clsx([
    'checkout-loyalty__field checkout__main__form__field',
    requiredError && formState.isSubmitted && 'checkout-loyalty__field--required',
  ]);

  return (
      <div className={className}>
        <label htmlFor={name}>
          <T id={label} /> <span className="required">*</span>
        </label>

        <select id={name} {...register(name, { required: true })}>
          <option value="" />
          {options.map(({ label, value }) => (
              <option key={value} value={value}>{label}</option>
          ))}
        </select>

        {requiredError && formState.isSubmitted && (
            <div className="validation-message">
              <T id="form.requiredField" />.
            </div>
        )}
      </div>
  );
};

const CheckoutLoyaltyMainRadio = ({ label, labelId, value, onChange, disabled, defaultChecked }) => {
  return (
      <div className="checkout-loyalty__radio">
        <input
            type="radio"
            id={value}
            name="option"
            value={value}
            disabled={disabled}
            onChange={onChange}
            defaultChecked={defaultChecked}
        />

        <label htmlFor={value}>{label || <T id={labelId} />}</label>
      </div>
  );
};

const LoyaltyConnectCard = () => {
  const { register, handleSubmit, formState: { errors, isSubmitted } } = useForm();

  const [connectCard, { loading }] = useCartMutation(CONNECT_CARD, {
    refetchQueries: [{ query: GET_CARD }],
  });

  const onSubmit = async data => {
    try {
      await connectCard({ variables: data });
    } catch (err) {
      console.error(err);
    }
  };

  return (
      <form className="checkout-loyalty__form" onSubmit={handleSubmit(onSubmit)}>
        <LoyaltyFormInput
            name="cardId"
            label="club.cardNumber2"
            register={register}
            errors={errors}
            formState={{ isSubmitted }}
        />

        <LoyaltyFormInput
            type="date"
            name="billId"
            label="club.dob"
            register={register}
            errors={errors}
            formState={{ isSubmitted }}
        />

        <div className="mt-12">
          <p>
            <T id="club.warning" />
          </p>
        </div>

        <div className="checkout-loyalty__checkbox">
          <input type="checkbox" id="iagree" {...register('iagree', { required: true })} />
          <label htmlFor="iagree">
            <T id="club.agree" />
          </label>
        </div>

        {loading && <Loader height="xl" mt="md" />}

        {!loading && (
            <button className="checkout__btn checkout__btn--loyalty mt-12">
              <T id="club.connectMyCard" />
            </button>
        )}

        <hr className="mt-12" />
      </form>
  );
};

const LoyaltyRegisterNewCard = () => {
  const { user } = useUserContext();
  const { register, handleSubmit, formState: { errors, isSubmitted } } = useForm();

  const [createCard, { loading }] = useCartMutation(CREATE_CARD, {
    refetchQueries: [{ query: GET_CARD }],
  });

  const onSubmit = async data => {
    try {
      data.isMobile = true;
      data.email = user.email;
      await createCard({ variables: data });
    } catch (err) {
      console.error(err);
    }
  };

  const countryOptions = [{ label: 'Hrvatska', value: 'HR' }, { label: 'Srbija', value: 'RS' }];
  const genderOptions = [{ label: 'Muški', value: 'male' }, { label: 'Ženski', value: 'female' }];

  const currYear = moment().format('YYYY');
  const minDate = `${currYear - 120}-01-01`;
  const maxDate = `${currYear}-01-01`;

  return (
      <form className="checkout-loyalty__form" onSubmit={handleSubmit(onSubmit)}>
        <LoyaltyFormInput
            name="firstname"
            label="club.name"
            register={register}
            errors={errors}
            formState={{ isSubmitted }}
        />

        <LoyaltyFormInput
            name="lastname"
            label="club.lastname"
            register={register}
            errors={errors}
            formState={{ isSubmitted }}
        />

        <LoyaltyFormInput
            name="street"
            label="club.street"
            register={register}
            errors={errors}
            formState={{ isSubmitted }}
        />

        <LoyaltyFormInput
            name="streetNumber"
            label="club.streetNumber"
            register={register}
            errors={errors}
            formState={{ isSubmitted }}
        />

        <LoyaltyFormInput
            name="zip"
            label="club.postcode"
            register={register}
            errors={errors}
            formState={{ isSubmitted }}
        />

        <LoyaltyFormInput
            name="city"
            label="club.city"
            register={register}
            errors={errors}
            formState={{ isSubmitted }}
        />

        <LoyaltyFormInputSelect
            name="country"
            label="club.country"
            options={countryOptions}
            register={register}
            errors={errors}
            formState={{ isSubmitted }}
        />

        <LoyaltyFormInput
            name="telephone"
            label="club.telephoneOrMobile"
            register={register}
            errors={errors}
            formState={{ isSubmitted }}
        />

        <LoyaltyFormInput
            type="date"
            name="dob"
            label="club.dob"
            min={minDate}
            max={maxDate}
            register={register}
            errors={errors}
            formState={{ isSubmitted }}
        />

        <LoyaltyFormInputSelect
            name="gender"
            label="club.gender"
            options={genderOptions}
            register={register}
            errors={errors}
            formState={{ isSubmitted }}
        />

        {loading && <Loader height="xl" mt="md" />}

        {!loading && (
            <button className="checkout__btn checkout__btn--loyalty mt-12">
              <T id="club.register" />
            </button>
        )}
      </form>
  );
};

const pointOptions = [
  { points: 50, percentage: 5, all: false },
  { points: 100, percentage: 6, all: false },
  { points: 200, percentage: 7, all: true },
  { points: 300, percentage: 8, all: true },
  { points: 400, percentage: 9, all: true },
  { points: 500, percentage: 10, all: true },
];

const CheckoutLoyaltyPointSelect = memo(({ saldoPoints }) => {
  const { cart } = useCartContext();
  const { t } = useTranslation();

  const [applyPoints, { loading }] = useCartMutation(APPLY_LOYALTY_POINTS);

  /** @type {React.ChangeEventHandler<HTMLInputElement>} */
  const onChangeHandler = event => {
    const points = +event.currentTarget.value;
    applyPoints({ variables: { points } });
  };

  const appliedPoints = cart && cart.extension_attributes && +cart.extension_attributes.loyalty_points_used;

  return (
      <form className="checkout-loyalty__point-select">
        <CheckoutLoyaltyMainRadio
            labelId="club.dontUsePoints"
            disabled={loading}
            value={0}
            onChange={onChangeHandler}
            defaultChecked={appliedPoints === 0}
        />

        {pointOptions.map(({ points, percentage, all }, idx) => {
          const label = t(all ? 'club.applyPointsOptionAll' : 'club.applyPointsOptionOne')
              .replace('[points]', points)
              .replace('[percentage]', percentage);

          return (
              <CheckoutLoyaltyMainRadio
                  key={idx}
                  label={label}
                  disabled={loading || saldoPoints < points}
                  value={points}
                  onChange={onChangeHandler}
                  defaultChecked={appliedPoints === points}
              />
          );
        })}

        <div className="checkout-loyalty__footnote">
          <p>
            * <T id="club.discountOneItem" />
          </p>
        </div>
      </form>
  );
});

const CheckoutLoyaltyMain = () => {
  const { data, loading } = useQuery(GET_CARD, {
    fetchPolicy: 'network-only',
  });

  const [option, setOption] = useState('');

  if (loading) {
    return <></>;
  }

  const { loyalty, success } = data && data.historyCustomerLoyaltyCard ? data.historyCustomerLoyaltyCard : {};

  if (success) {
    const { cardID, saldoPoints } = loyalty[0];

    return (
        <div className="">
          <div className="checkout-loyalty__card-info">
            <p>
              <T id="club.yourCardNumber" />: {cardID}
            </p>
            <p>
              <T id="club.numberOfPoints" />: {saldoPoints} <T id="club.points" />
            </p>
          </div>

          {saldoPoints < 50 && (
              <div className="checkout-loyalty__no-points">
                <p>
                  <T id="club.insufficientPoints" />
                </p>

                <p>
                  <T id="club.collectingPoints" />
                  <br />
                  <T id="club.collectedPoints" />{' '}
                  <Link to="/customer/account">
                    <T id="club.yourProfile" />
                  </Link>
                  .
                </p>
              </div>
          )}

          {saldoPoints >= 50 && <CheckoutLoyaltyPointSelect saldoPoints={saldoPoints} />}
        </div>
    );
  }

  return (
      <div className="">
        <CheckoutLoyaltyMainRadio
            labelId="club.connectExistingCard"
            value="connect"
            onChange={e => setOption(e.target.value)}
        />

        {option === 'connect' && <LoyaltyConnectCard />}

        <CheckoutLoyaltyMainRadio
            labelId="club.createNewCard"
            value="newcard"
            onChange={e => setOption(e.target.value)}
        />

        {option === 'newcard' && <LoyaltyRegisterNewCard />}
      </div>
  );
};

const CheckoutLoyalty = () => {
  const { user } = useUserContext();

  return (
      <div className="checkout-loyalty">
        <div className="checkout-loyalty__header">
          <img className="header" src="/images/checkout/product-page-emm-club-header.png" alt="header-logo" />

          <img className="header-logo" src="images/checkout/klub_kartica.png" alt="loyalty-card" />
        </div>

        <div className="checkout-loyalty__body">
          {!user && (
              <div className="checkout-loyalty__middle">
                <p className="loyalty-message">
                  <T id="cart.registration" />
                  <strong>
                    <T id="cart.emmezetaClub" />
                  </strong>
                  <T id="cart.registration2" />
                </p>
              </div>
          )}

          <div className="checkout-loyalty__bottom">
            <CheckoutLoyaltyPoints />

            {!user && <CheckoutLoyaltyRegister />}
          </div>

          {user && <CheckoutLoyaltyMain />}
        </div>
      </div>
  );
};

export default memo(CheckoutLoyalty);